<script setup>
import HospitalList from "@/components/hpo/dashboard/hospital/HospitalList";
import {useQuery} from "@vue/apollo-composable";
import {useLastResult} from "@/graphql/composable";
import {HpoHospitalList} from "@/graphql/queries/hpo/hpoHospitalList.graphql"
import {HospitalScreenPatientListQuery} from "@/graphql/queries/hpo/patients.graphql"
import {computed, onBeforeUnmount, onMounted, ref} from "vue";
import CountsBlock from "@/components/hpo/dashboard/CountsBlock";
import dayjs from "dayjs";
import HospitalScreenPatientList from "./patientListItems/HospitalScreenPatientList.vue";
import {useDisplay} from "vuetify";
import {pgettext} from "@/utils/translate";


const {smAndDown} = useDisplay()

const props = defineProps({
  hpo: {
    type: Object,
    required: true
  },
  member: {
    type: Object,
    required: true
  },
  patientCounts: {
    type: Object,
    required: true
  },
  patientCountsLoading: {
    type: Boolean,
    required: true
  },
  hpoPassword: {
    type: String,
    required: true
  },
  countries: {
    type: Array,
    required: true
  },
  mobileView: {
    type: Boolean,
    required: true,
  },
  chipProperties: {
    type: Object,
    required: true
  },
  personsTrackerTimestamp: {
    type: Object,
    required: true
  },
  hospitalsTrackerTimestamp: {
    type: Object,
    required: false
  }
})

const now = ref(new dayjs())

// Persons Query
const personsQueryVariables = computed(() => ({
  updateTimestamp: props.personsTrackerTimestamp,
  hpoId: props.hpo.id,
  hospitalIds: props.member.hospitals
}));
const {
  result: patientListResult,
  loading: patientListLoading
} = useQuery(HospitalScreenPatientListQuery, personsQueryVariables)
const patientList = useLastResult(patientListResult, [], data => data.hsPatientList)
const sortedPatientList = computed(() => {
  return [...patientList.value].sort((a, b) => {
    if (a.eta == null && b.eta == null) return 0
    if (a.eta == null) return 1
    if (b.eta == null) return -1
    return new Date(a.eta) - new Date(b.eta)
  })
})


// Hospitals Query
const hospitalsQueryVariables = computed(() => ({
  updateTimestamp: props.hospitalsTrackerTimestamp,
  hpoId: props.hpo.id
}));
const {
  result: hospitalsResult,
  loading: hospitalListLoading
} = useQuery(HpoHospitalList, hospitalsQueryVariables)
const hospitalList = useLastResult(hospitalsResult, [], data => data.hpoHospitalList)

const anyLoading = computed(() => {
  return hospitalListLoading.value || patientListLoading.value || props.patientCountsLoading
})


let updateNowInterval = null

function updateNow() {
  updateNowInterval = setInterval(() => {
    now.value = new dayjs()
  }, 5000)
}


onMounted(() => {
  updateNow()
})

onBeforeUnmount(() => {
  if (updateNowInterval) {
    clearInterval(updateNowInterval)
  }
})

</script>

<template>
  <v-container fluid class="pa-0">
    <v-row dense>
      <v-col class="d-flex ga-3 flex-wrap">
        <v-expansion-panels v-if="smAndDown">
          <v-expansion-panel :title="pgettext('label','Counters')">
            <template #text>
              <CountsBlock :hpoId="hpo.id"
                           :title="pgettext('label', 'Estimated on scene')"
                           :member="member"
                           :loading="patientCountsLoading"
                           :patientCounts="patientCounts.onScene"
                           :legend="pgettext('label', 'Estimated number of persons on scene')"/>

              <HospitalList :hpoId="hpo.id"
                            :hospitalList="hospitalList"
                            :member="member"/>
            </template>

          </v-expansion-panel>
        </v-expansion-panels>
        <template v-else>
          <CountsBlock :hpoId="hpo.id"
                       :title="pgettext('label', 'Estimated on scene')"
                       :member="member"
                       :loading="patientCountsLoading"
                       :patientCounts="patientCounts.onScene"
                       :legend="pgettext('label', 'Estimated number of persons on scene')"/>

          <HospitalList :hpoId="hpo.id"
                        :hospitalList="hospitalList"
                        :member="member"/>
        </template>

      </v-col>
    </v-row>

    <v-row dense>
      <v-col cols="12">
        <v-card>
          <v-card-title>
            <h2 class="text-center">{{ pgettext('label', 'Patients on their way') }}</h2>
          </v-card-title>
          <v-card-text>
            <HospitalScreenPatientList :patientList="sortedPatientList"
                                       :hpo="hpo"
                                       :member="member"
                                       :anyLoading="anyLoading"
                                       :hospitalList="hospitalList"
                                       :now="now"/>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>